import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { TeamMemberScore } from "@shared/types";

interface TeamScorecardProps {
  locationId: string;
  organizationId?: string;
}

export function TeamScorecard({ locationId, organizationId }: TeamScorecardProps) {
  // Build API URL with organization parameter for multi-organization support
  const buildApiUrl = () => {
    let url = `/api/scorecards?locationId=${encodeURIComponent(locationId)}`;
    if (organizationId) {
      url += `&organizationId=${organizationId}`;
    }
    return url;
  };

  // This would be a real API call in production
  const { data: teamMembers, isLoading } = useQuery<TeamMemberScore[]>({
    queryKey: [buildApiUrl(), organizationId],
    enabled: Boolean(organizationId && locationId && locationId !== 'all'),
  });

  // Status indicator colors
  const getStatusIndicator = (status: string) => {
    switch (status) {
      case 'green':
        return 'bg-success';
      case 'yellow':
        return 'bg-warning';
      case 'red':
        return 'bg-destructive';
      default:
        return 'bg-neutral-400';
    }
  };

  const data = teamMembers || [];

  return (
    <Card className="p-4">
      <CardHeader className="p-0 pb-4 flex flex-row space-y-0 justify-between items-center gap-2">
        <CardTitle className="text-lg font-medium text-left">Team Scorecard</CardTitle>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
          <span className="mdi mdi-dots-vertical text-neutral-500"></span>
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, index) => (
              <Skeleton key={index} className="h-6 w-full" />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {data.length === 0 && <p className="text-sm text-slate-600 dark:text-slate-300">{locationId === 'all' ? 'Select one location to view its scorecard.' : 'No scorecard yet.'}</p>}
            {data.map((member) => (
              <div key={member.id} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className={`w-2 h-2 rounded-full ${getStatusIndicator(member.status)} mr-2`}></div>
                  <span className="text-sm">{member.name}</span>
                </div>
                <div className="text-sm font-medium">{member.score}%</div>
              </div>
            ))}
            
            <Button variant="ghost" className="mt-2 w-full">
              <span className="mdi mdi-account-group mr-1"></span>
              View All Team Members
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
